import React, { useState, useEffect, useRef } from 'react';
import { toggleVapiCall, subscribeVapiCall } from '../utils/vapiVoice';
import './AISalesChat.css';

const AI_SERVICE_BASE_URL =
  process.env.REACT_APP_AI_SERVICE_URL ||
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8000'
    : 'https://api.lacegota.in');

const INITIAL_GREETING =
  "Namaste! Welcome to SGTB LACE. Main products, prices, colors/designs aur pack/meter calculation mein help kar sakta hoon. Aap kya dhoondh rahe hain?";

const getOrCreateSessionId = () => {
  try {
    let sid = localStorage.getItem('sgtb_ai_session_id');
    if (!sid) {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        sid = `web_${crypto.randomUUID()}`;
      } else {
        sid = `web_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }
      localStorage.setItem('sgtb_ai_session_id', sid);
    }
    return sid;
  } catch (e) {
    return `web_${Date.now()}`;
  }
};

const getPageContext = () => {
  if (typeof window === 'undefined') {
    return { pathname: '', page_type: null, category_slug: null, product_slug: null };
  }
  const pathname = window.location.pathname || '';
  const categoryMatch = pathname.match(/^\/category\/([^/?#]+)/);
  const productMatch = pathname.match(/^\/product\/([^/?#]+)/);
  return {
    pathname,
    page_type: categoryMatch ? 'category' : (productMatch ? 'product' : null),
    category_slug: categoryMatch ? decodeURIComponent(categoryMatch[1]) : null,
    product_slug: productMatch ? decodeURIComponent(productMatch[1]) : null
  };
};

const AISalesChat = () => {
  const [sessionId] = useState(getOrCreateSessionId);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showMobilePrompt, setShowMobilePrompt] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'init_greeting',
      sender: 'ai',
      text: INITIAL_GREETING,
      products: [],
      category: null,
      calculation: null,
      price_range: null,
      actions: [],
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputMsg, setInputMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Subscribe to Vapi Voice Calling state
  useEffect(() => {
    const unsubscribe = subscribeVapiCall(setIsCalling);
    return () => unsubscribe();
  }, []);

  // Listen for custom open event from FloatingSupport
  useEffect(() => {
    const handleOpenEvent = () => {
      setIsOpen(true);
      setIsMinimized(false);
      setShowMobilePrompt(false);
    };
    window.addEventListener('open_sgtb_ai_chat', handleOpenEvent);
    return () => window.removeEventListener('open_sgtb_ai_chat', handleOpenEvent);
  }, []);

  // Broadcast state change to FloatingSupport
  useEffect(() => {
    let state = 'closed';
    if (isOpen) {
      state = isMinimized ? 'minimized' : 'open';
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sgtb_ai_chat_state_change', { detail: { state } }));
    }
  }, [isOpen, isMinimized]);

  // Auto-open logic (ONCE per browser session)
  useEffect(() => {
    const isDismissed = sessionStorage.getItem('sgtb_ai_chat_closed') === 'true';
    if (isDismissed) return;

    const timer = setTimeout(() => {
      if (window.innerWidth <= 768) {
        setShowMobilePrompt(true);
      } else {
        setIsOpen(true);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    if (isOpen && messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isLoading]);

  const handleClose = () => {
    setIsOpen(false);
    setShowMobilePrompt(false);
    sessionStorage.setItem('sgtb_ai_chat_closed', 'true');
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const sendMessageWithPayload = async (payload) => {
    if (isLoading) return;
    setIsLoading(true);

    const userText = payload.message || '';
    if (userText.trim()) {
      const userMsgObj = {
        id: `usr_${Date.now()}`,
        sender: 'user',
        text: userText,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, userMsgObj]);
    }

    try {
      const requestBody = {
        session_id: sessionId,
        page_context: getPageContext(),
        ...payload
      };

      const res = await fetch(`${AI_SERVICE_BASE_URL}/web-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Request failed');

      const aiMsgObj = {
        id: `ai_${Date.now()}`,
        sender: 'ai',
        text: data.reply || 'Here is the requested information.',
        products: data.products || [],
        category: data.category || null,
        calculation: data.calculation || null,
        price_range: data.price_range || null,
        actions: data.actions || [],
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages((prev) => [...prev, aiMsgObj]);
    } catch (err) {
      console.error('AI Sales Chat Error:', err);
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          sender: 'ai',
          text: "Sorry, I couldn't connect right now. Please try again or chat with us on WhatsApp.",
          isError: true,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setIsLoading(false);
      setInputMsg('');
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleFormSubmit = (e) => {
    if (e) e.preventDefault();
    if (!inputMsg.trim() || isLoading) return;
    sendMessageWithPayload({ message: inputMsg.trim() });
  };

  const handleProductSelect = (optionNumber) => {
    sendMessageWithPayload({
      action: { type: 'select_product', option_number: optionNumber }
    });
  };

  const handleShowMore = (offset) => {
    sendMessageWithPayload({
      action: { type: 'show_more', offset: offset }
    });
  };

  const handleCategoryViewAll = (categoryName) => {
    sendMessageWithPayload({
      action: { type: 'category_view_all', category_name: categoryName }
    });
  };

  const renderFormattedText = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, idx) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="ai-chat-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="ai-sales-chat-container">
      {/* MOBILE WELCOME PROMPT */}
      {showMobilePrompt && !isOpen && (
        <div
          className="ai-chat-mobile-prompt"
          onClick={() => { setIsOpen(true); setShowMobilePrompt(false); }}
          role="button"
          tabIndex={0}
          aria-label="Open Chat with AI"
        >
          <span>Looking for a design? Ask AI 👋</span>
          <button
            className="prompt-close"
            onClick={(e) => {
              e.stopPropagation();
              setShowMobilePrompt(false);
              sessionStorage.setItem('sgtb_ai_chat_closed', 'true');
            }}
            aria-label="Dismiss prompt"
            title="Dismiss prompt"
          >
            ✕
          </button>
        </div>
      )}

      {/* CHAT PANEL */}
      {isOpen && (
        <div className={`ai-chat-panel ${isMinimized ? 'minimized' : ''}`}>
          {/* HEADER */}
          <div className="ai-chat-header">
            <div className="header-main-row">
              <div className="header-info">
                <span className="status-dot"></span>
                <span className="header-title">
                  {isMinimized ? '💬 SGTB AI Chat' : 'SGTB AI Sales Assistant'}
                </span>
              </div>
              <div className="header-actions">
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleMinimize}
                  title={isMinimized ? "Expand" : "Minimize"}
                  aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
                >
                  {isMinimized ? '□' : '–'}
                </button>
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleClose}
                  title="Close"
                  aria-label="Close chat"
                >
                  ✕
                </button>
              </div>
            </div>

            {!isMinimized && (
              <div className="header-voice-row">
                <button
                  type="button"
                  className={`header-voice-cta ${isCalling ? 'active' : ''}`}
                  onClick={toggleVapiCall}
                  aria-label="Talk to AI Manager"
                  title="Talk To AI (Voice Assistant)"
                >
                  <span className="voice-mic-icon">{isCalling ? '🛑' : '🎙'}</span>
                  <span>{isCalling ? 'End Call' : 'Talk To AI'}</span>
                </button>
              </div>
            )}
          </div>

          {/* CHAT BODY */}
          {!isMinimized && (
            <>
              <div className="ai-chat-body">
                {messages.map((msg) => (
                  <div key={msg.id || msg.time} className={`chat-bubble-row ${msg.sender}`}>
                    {msg.sender === 'ai' && <div className="chat-avatar">🤖</div>}
                    <div className={`chat-bubble ${msg.sender} ${msg.isError ? 'error-bubble' : ''}`}>
                      <div className="bubble-content">{renderFormattedText(msg.text)}</div>

                      {/* PRICE RANGE CARD */}
                      {msg.price_range && (
                        <div className="ai-price-range-card">
                          <div className="price-range-header">
                            🏷️ Price Range {msg.category ? `(${msg.category.name})` : ''}
                          </div>
                          <div className="price-range-values">
                            <strong>₹{msg.price_range.min_price}</strong> — <strong>₹{msg.price_range.max_price}</strong> <span className="unit-label">/ pack</span>
                          </div>
                        </div>
                      )}

                      {/* STRUCTURED PRODUCT CARDS GRID */}
                      {msg.products && msg.products.length > 0 && (
                        <div className="ai-products-grid">
                          {msg.products.map((prod) => (
                            <div key={prod.option_number} className="ai-product-card">
                              <div className="product-card-image-wrap">
                                <img
                                  src={prod.image || '/placeholder.jpg'}
                                  alt={prod.title}
                                  className="product-card-img"
                                  onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                                />
                                <span className="product-card-badge">#{prod.option_number}</span>
                              </div>
                              <div className="product-card-info">
                                <div className="product-card-title">{prod.title}</div>
                                <div className="product-card-price-row">
                                  <span className="product-card-price">₹{prod.price}</span>
                                  {prod.regular_price > prod.price && (
                                    <span className="product-card-reg-price">₹{prod.regular_price}</span>
                                  )}
                                  <span className="product-card-unit">({prod.pack_text || '1 Pack'})</span>
                                </div>
                                {prod.color_count > 0 && (
                                  <div className="product-card-meta">
                                    {prod.color_count} {prod.color_count > 1 ? 'colors available' : 'color available'}
                                  </div>
                                )}
                                <div className="product-card-actions">
                                  {prod.storefront_url && (
                                    <a
                                      href={prod.storefront_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="card-btn view-btn"
                                      aria-label={`View ${prod.title}`}
                                      title={`View ${prod.title}`}
                                    >
                                      View Product
                                    </a>
                                  )}
                                  <button
                                    type="button"
                                    className="card-btn select-btn"
                                    onClick={() => handleProductSelect(prod.option_number)}
                                    aria-label={`Select option ${prod.option_number}`}
                                    title={`Select option ${prod.option_number}`}
                                  >
                                    Select #{prod.option_number}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* SHOW MORE BUTTON */}
                      {msg.products && msg.products.length > 0 && (msg.category ? msg.category.has_more : true) && (
                        <div className="show-more-btn-row">
                          <button
                            type="button"
                            className="show-more-btn"
                            onClick={() => handleShowMore((msg.category && msg.category.next_offset) || msg.products.length)}
                            aria-label="Show 3 More designs"
                          >
                            ✨ Show 3 More
                          </button>
                        </div>
                      )}

                      {/* CATEGORY VIEW ALL BUTTON */}
                      {msg.category && (msg.category.category_url || msg.category.url) && (
                        <div className="category-btn-row">
                          <a
                            href={msg.category.category_url || msg.category.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="category-action-btn"
                            aria-label={`View All ${msg.category.name}`}
                          >
                            🛍️ View All {msg.category.name} ({msg.category.category_design_count || msg.category.cat_total || msg.category.count || 0} Designs)
                          </a>
                        </div>
                      )}

                      {/* STRUCTURED CALCULATION CARD */}
                      {msg.calculation && (
                        <div className="ai-calculation-card">
                          <div className="calc-header">
                            📊 Order Calculation Summary
                          </div>
                          <div className="calc-row">
                            <span>Requirement:</span>
                            <strong>
                              {msg.calculation.requested_quantity} {msg.calculation.requested_unit}s
                            </strong>
                          </div>
                          <div className="calc-row">
                            <span>Packs Required:</span>
                            <strong>
                              {msg.calculation.packs_required} Packs ({msg.calculation.total_supplied_meters} Meters total)
                            </strong>
                          </div>
                          <div className="calc-row">
                            <span>Price per pack:</span>
                            <span>₹{msg.calculation.price_per_pack}</span>
                          </div>
                          <div className="calc-row calc-total">
                            <strong>Estimated Total:</strong>
                            <strong className="total-val">₹{msg.calculation.estimated_total}</strong>
                          </div>

                          <div className="calc-actions">
                            <button
                              type="button"
                              className="calc-btn proceed-btn"
                              onClick={() => sendMessageWithPayload({ message: 'yes' })}
                              aria-label="Proceed with quantity"
                            >
                              Proceed
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ERROR WHATSAPP FALLBACK LINK */}
                      {msg.isError && (
                        <div className="whatsapp-fallback-container">
                          <a
                            href="https://wa.me/918287691638"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whatsapp-fallback-btn"
                          >
                            💬 Continue on WhatsApp
                          </a>
                        </div>
                      )}

                      <div className="bubble-time">{msg.time}</div>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="chat-bubble-row ai">
                    <div className="chat-avatar">🤖</div>
                    <div className="chat-bubble ai loading-bubble">
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* FOOTER INPUT */}
              <form className="ai-chat-footer" onSubmit={handleFormSubmit}>
                <textarea
                  ref={inputRef}
                  className="ai-chat-input"
                  placeholder="Ask about products, prices, colors..."
                  value={inputMsg}
                  onChange={(e) => setInputMsg(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleFormSubmit();
                    }
                  }}
                  rows={1}
                  aria-label="Chat input"
                />
                <button
                  type="submit"
                  className="ai-chat-send-btn"
                  disabled={!inputMsg.trim() || isLoading}
                  aria-label="Send message"
                  title="Send message"
                >
                  ➤
                </button>
              </form>
              <div className="ai-chat-footer-note">
                <a
                  href="https://wa.me/918287691638"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wa-link-small"
                >
                  💬 Or Chat on WhatsApp (+91 82876 91638)
                </a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AISalesChat;
